import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot } from "@angular/router";
import { BehaviorSubject, Observable, of } from "rxjs";

import { EnvironmentService } from "../environment/environment.service";
import { SessionService } from "../session/session.service";
import { AuthUser } from "./auth-frame/auth-user.model";
import { BootstrapService } from "../bootstrap.service";
import { AlertService } from "../../shared/alert/alert.service";
import { ItauUserIdleService } from "../security/itau-user-idle.service";
import { mergeMap, retryWhen } from 'rxjs/operators';

const gestorTokenUrl = "/api/oauth/gestorToken?code=";
const signOnOk4 = "/ok4menu/Login.aspx/LoginEn4";
const ok4Logout = "/ok4menu/Login.aspx/SairEn4";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private apiUrl: string;
  private apiUrlMenu: string;
  private apiOK4: string;
  private authSettings: any;
  private redirectUriPath: string = "";
  private idleUserValidationHasStarted: boolean = false;
  private authUser: AuthUser;
  private authSubject: BehaviorSubject<AuthUser> =
    new BehaviorSubject<AuthUser>(null);

  constructor(
    private environmentService: EnvironmentService,
    private http: HttpClient,
    private sessionService: SessionService,
    private bootstrapService: BootstrapService,
    private alertService: AlertService,
    private userIdleService: ItauUserIdleService
  ) {
    this.apiUrl = this.environmentService.envModel.appSettings.apiUrlAuth;
    this.apiUrlMenu = this.environmentService.envModel.appSettings.apiUrlMenu;
    this.apiOK4 = this.environmentService.envModel.appSettings.apiOK4;
    this.authSettings = this.environmentService.envModel.authSettings;
  }

  buildUrl(): string {
    return `${this.authSettings.openIdUrl}?client_id=${this.authSettings.client_id}&redirect_uri=${this.authSettings.redirect_uri}${this.pathValue}&response_type=${this.authSettings.response_type}&scope=${this.authSettings.scope}`;
  }

  getAuthModel(): Observable<AuthUser> {
    this.authSubject.next(this.sessionService.getAuthUser());
    return this.authSubject;
  }

  public get currentUserValue(): AuthUser {
    this.authSubject.next(this.sessionService.getAuthUser());
    return this.authSubject.value;
  }

  get authenticatedUser(): AuthUser {
    return this.authSubject.value;
  }

  retrieveUserCredentials(token: string): Observable<any> {
    let headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
    });
    let options: any = {
      headers: headers,
      responseType: "text",
      withCredentials: true,
    };
    return this.http.post(this.apiOK4 + signOnOk4, { token: token }, options);
  }

  createAccessModel(token: string): boolean {
    this.retrieveUserCredentials(token).subscribe(
      (response: any) => {
        let result = response.toString();
        let jsonError = result.indexOf("_Exception");
        let jsonStartIndex = result.indexOf("{");
        let jsonEndIndex = result.indexOf("</div>");
        if (jsonError < 0) {
          this.authUser = JSON.parse(
            result.substring(jsonStartIndex - 1, jsonEndIndex - 1)
          ).authUser;
          this.sessionService.setAuthUser(this.authUser);
          this.authSubject.next(this.authUser);
          this.bootstrapService.bootstrap(token);
          this.sessionService.setSessionState("true");
          this.sessionService.setToken(token);
          this.startIdleUserValidation();
        } else {
          this.alertService.openInfoSessionModal(2, "br");
        }
      },
      (err) => {
        this.alertService.openInfoSessionModal(2, "br");
      }
    );
    return true;
  }

  invalidateAccessModel(shouldRedirect: boolean): void {
    this.expireSession();

    if (shouldRedirect) {
      this.redirectToLogout();
    }
  }

  retrieveAccessModel(): string {
    return this.sessionService.retrieveToken();
  }

  validateAccessModel(): Observable<boolean> {
    let token = this.sessionService.retrieveToken();
    if (token && this.sessionService.verifySessionState()) {
      if (!this.idleUserValidationHasStarted) {
        this.startIdleUserValidation();
      }
      return this.http.get<boolean>(this.apiUrl + gestorTokenUrl + token, {
        withCredentials: true,
      });
    }

    this.sessionService.setSessionState("false");
    return null;
  }

  closeConectionOk4(): Observable<any> {
    let headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
    });
    let options: any = { headers: headers, responseType: "text" };
    let currentUser = this.sessionService.getAuthUser();

    if (
      !currentUser ||
      !currentUser.name ||
      !currentUser.idConexao ||
      !currentUser.idSessao
    ) {
      return Observable.create((obs) => {
        obs.next(true);
        obs.complete();
      });
    }

    return this.http.post(
      this.apiOK4 + ok4Logout,
      {
        NomeUsuario: currentUser.name,
        Id: currentUser.idConexao,
        IdSessao: currentUser.idSessao,
      },
      options
    );
  }

  logout(shouldRedirect: boolean): void {
    let invalid = false;
    this.closeConectionOk4().subscribe(
      () => {
        invalid = true;
        this.invalidateAccessModel(shouldRedirect);
      },
      () => {
        invalid = true;
        this.invalidateAccessModel(shouldRedirect);
      },
      () => {
        if (!invalid) this.invalidateAccessModel(shouldRedirect);
      }
    );
  }

  openInvalidSessionModal() {
    this.alertService.openInfoSessionModal(1, "br");
  }

  startIdleUserValidation() {
    this.userIdleService.startWatching();
    this.userIdleService.onTimerStart().subscribe();
    this.idleUserValidationHasStarted = true;
    this.idleUserOnTimeout();
    this.verifyIdleUser();
  }

  expireSession() {
    this.sessionService.expireSession();
    this.userIdleService.stopTimer();
    this.userIdleService.stopWatching();
    this.idleUserValidationHasStarted = false;
  }

  idleUserOnTimeout() {
    this.userIdleService.onTimeout().subscribe(() => {
      this.logout(false);
      this.openInvalidSessionModal();
    });
  }

  authenticateOnOpenId() {
    location.assign(this.apiUrl + "/login");
  }

  errorLogin() {
    this.alertService.openInfoSessionModal(2, "br");
  }

  verifyIdleUser() {
    this.userIdleService.ping$.subscribe(() => {
      this.pingSessionState();
    });
  }

  pingSessionState(): void {
    let sessionExpired = this.sessionService.isSessionExpired;
    let sessionState = this.sessionService.verifySessionState();
    let idConexao =
      this.sessionService.getAuthUser() === null ||
      this.sessionService.getAuthUser() === undefined
        ? null
        : this.sessionService.getAuthUser().idConexao;
    if (!sessionExpired) {
      this.sessionService
        .getSessionState(this.sessionService.retrieveToken(), idConexao)
        .pipe(
          retryWhen(errors =>
            errors.pipe(
              mergeMap((error, i) => {
                if (error.status === 500 && i < 10) {
                  return of(error);
                }
              })
            )
          )
        )
        .subscribe(
          (backSession) => {
            this.sessionService.setSessionState(sessionState.toString());
            if ((!sessionState && !sessionExpired) || !backSession) {
              this.expireSession();
              this.openInvalidSessionModal();
            }
          },
          () => {
            this.expireSession();
            this.openInvalidSessionModal();
          }
        );
    }
  }

  getTokenFromURL(query: string, route: ActivatedRouteSnapshot): string {
    const regex: RegExp = new RegExp("[,;&\n]", "g");
    const param: string = "token=";

    if (route.fragment && !query) {
      let routeParam = route.fragment;
      return this.retrieveParameterList(routeParam, regex, param);
    }

    if (query && query.indexOf(param) >= 0) {
      return this.retrieveParameterList(query, regex, param);
    }
    return null;
  }

  redirectToLogout() {
    let urlOpenId = this.environmentService.envModel.appSettings.apiUrlLogout;
    location.assign(urlOpenId);
  }

  get pathValue(): string {
    return this.redirectUriPath;
  }

  private retrieveParameterList(content: any, regex: RegExp, param: string) {
    let index = content.indexOf(param);
    let code = content.substring(index + 6);
    let list = code.split(regex);
    return list[0];
  }
}
